import type { ProductSizeOption } from '@repo/api-contracts';
import { parseQuoteQuantity } from './quote-request-rules';

export { SIZE_TOTAL_EMPTY_MESSAGE, SIZE_TOTAL_INVALID_MESSAGE } from './quote-request-rules';

export interface SizeQuantityRow {
  readonly sizeOptionId: string;
  readonly quantity: string;
}

/** 사이즈 id → 입력 문자열. 빈칸은 총 수량 입력에서만 0이다. */
export type SizeFieldValues = Readonly<Record<string, string>>;

type SizeTotals = readonly { readonly sizeOptionId: string; readonly quantity: number }[];
type SizeCards = readonly { readonly sizes: readonly SizeQuantityRow[] }[];

export const sizeTotalsFromFields = (
  options: readonly ProductSizeOption[],
  fields: SizeFieldValues,
): { readonly bySize: SizeTotals; readonly total: number } | null => {
  const bySize: { sizeOptionId: string; quantity: number }[] = [];
  let total = 0;
  for (const option of options) {
    const raw = fields[option.id]?.trim() ?? '';
    const quantity = raw === '' ? 0 : parseQuoteQuantity(raw);
    if (!Number.isInteger(quantity) || quantity < 0) return null;
    bySize.push({ sizeOptionId: option.id, quantity });
    total += quantity;
  }
  return { bySize, total };
};

export const allocationForSingleDestination = (bySize: SizeTotals): readonly SizeQuantityRow[] =>
  bySize
    .filter((row) => row.quantity > 0)
    .map((row) => ({
      sizeOptionId: row.sizeOptionId,
      quantity: String(row.quantity),
    }));

export const sizeRowsTotal = (rows: readonly SizeQuantityRow[]): number | null => {
  let total = 0;
  for (const row of rows) {
    const quantity = parseQuoteQuantity(row.quantity);
    if (!Number.isInteger(quantity) || quantity < 1) return null;
    total += quantity;
  }
  return total;
};

export const remainingBySize = (
  options: readonly ProductSizeOption[],
  bySize: SizeTotals,
  cards: SizeCards,
): readonly {
  readonly sizeOptionId: string;
  readonly sizeName: string;
  readonly remaining: number;
}[] =>
  options.map((option) => {
    const total = bySize.find((row) => row.sizeOptionId === option.id)?.quantity ?? 0;
    const allocated = cards.reduce(
      (sum, card) =>
        sum +
        card.sizes.reduce((subtotal, row) => {
          if (row.sizeOptionId !== option.id) return subtotal;
          const quantity = parseQuoteQuantity(row.quantity);
          return subtotal + (Number.isInteger(quantity) ? quantity : 0);
        }, 0),
      0,
    );
    return { sizeOptionId: option.id, sizeName: option.name, remaining: total - allocated };
  });

export const checkSizeAllocation = (
  options: readonly ProductSizeOption[],
  bySize: SizeTotals,
  cards: SizeCards,
): string | null => {
  const remaining = remainingBySize(options, bySize, cards);
  const unallocated = remaining.filter((row) => row.remaining > 0);
  if (unallocated.length > 0) {
    return `배송지에 배정하지 않은 수량이 있습니다: ${unallocated.map((row) => `${row.sizeName} ${row.remaining}개`).join(' · ')}`;
  }
  const excess = remaining.filter((row) => row.remaining < 0);
  return excess.length > 0
    ? `사이즈별 수량보다 많이 배정했습니다: ${excess.map((row) => `${row.sizeName} ${Math.abs(row.remaining)}개`).join(' · ')}`
    : null;
};

/** 단일 배송지는 총 수량을 배분하고, 여러 배송지는 입력한 배분으로 제출 수량을 맞춘다. */
export const applySizeAllocation = (
  options: readonly ProductSizeOption[],
  bySize: SizeTotals,
  items: readonly {
    readonly key: string;
    readonly value: { readonly quantity: string; readonly sizes: readonly SizeQuantityRow[] };
  }[],
): readonly {
  readonly key: string;
  readonly sizes: readonly SizeQuantityRow[];
  readonly quantity: string;
}[] =>
  items.map((item) => {
    if (options.length === 0) return { key: item.key, sizes: [], quantity: item.value.quantity };
    const sizes = items.length === 1 ? allocationForSingleDestination(bySize) : item.value.sizes;
    const total = sizeRowsTotal(sizes);
    return { key: item.key, sizes, quantity: total === null ? '' : String(total) };
  });
